// Appointment — create / edit (reschedule) a booking.
// Two-column dialog: rich service picker + date + animated slot grid on the
// left, customer details (phone w/ country code) on the right. Slots
// re-validate server-side on save, so a stale pick just errors politely.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Clock, User, StickyNote } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { PhoneField } from '@/components/PhoneField';
import {
  appointmentsService, to12h, ymd,
  type ApptBooking, type ApptService, type ApptSlot,
} from '@/services/appointments.service';
import ServicePicker from './ServicePicker';
import SaveProgressOverlay from './SaveProgressOverlay';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  services: ApptService[];
  booking?: ApptBooking | null;   // edit / reschedule mode
  defaultDate?: string;           // yyyy-MM-dd (calendar day click)
}

const Label = ({ icon: Icon, step, children }: { icon?: typeof Clock; step?: number; children: React.ReactNode }) => (
  <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-2">
    {step != null && (
      <span className="w-[18px] h-[18px] rounded-full bg-primary-soft text-primary text-[10px] font-bold flex items-center justify-center">{step}</span>
    )}
    {Icon && step == null && <Icon size={12} />} {children}
  </label>
);

const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={clsx(
      'relative h-5.5 w-10 shrink-0 rounded-full transition-colors h-[22px]',
      checked ? 'bg-primary' : 'bg-border-strong',
    )}
  >
    <span className={clsx(
      'absolute top-[3px] h-4 w-4 rounded-full bg-white shadow-soft transition-all',
      checked ? 'left-[21px]' : 'left-[3px]',
    )} />
  </button>
);

const BookingDialog = ({ open, onClose, onSaved, services, booking, defaultDate }: Props) => {
  const edit = !!booking;
  const [serviceUid, setServiceUid] = useState('');
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState<ApptSlot[]>([]);
  const [slotLoading, setSlotLoading] = useState(false);
  const [slot, setSlot] = useState<ApptSlot | null>(null);
  // a day with nothing to book says WHY (closed on Sundays, in the past, fully booked…) and offers the next days
  // that have times; a New booking opened without a day simply moves to the first of them
  const [slotReason, setSlotReason] = useState<string | null>(null);
  const [slotError, setSlotError] = useState<string | null>(null);
  const [nextDates, setNextDates] = useState<Array<{ date: string; weekday: string; slotCount: number }>>([]);
  const dateChosen = useRef(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');   // E.164 ("+9715…") or ''
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [autoConfirm, setAutoConfirm] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayPhase, setOverlayPhase] = useState<'processing' | 'complete'>('processing');

  type BkErrors = { service?: string; date?: string; slot?: string; name?: string; phone?: string; email?: string };
  const [errors, setErrors] = useState<BkErrors>({});
  const clearErr = (k: keyof BkErrors) => setErrors((p) => (p[k] ? { ...p, [k]: undefined } : p));

  useEffect(() => {
    if (!open) return;
    setServiceUid(booking?.serviceUid || ''); // create: user picks the service explicitly
    // a calendar click on a past day opens on today — a past day never has a time to book
    const today = ymd(new Date());
    const d0 = booking?.bookingDate || defaultDate || today;
    setDate(!booking && d0 < today ? today : d0);
    dateChosen.current = !!booking || (!!defaultDate && defaultDate >= today);
    setSlot(booking ? { start: booking.slotStart, end: booking.slotEnd, available: true, remaining: 1 } : null);
    setName(booking?.customerName || '');
    setPhone(booking?.customerPhone ? `+${booking.customerPhone.replace(/^\+/, '')}` : '');
    setEmail(booking?.customerEmail || '');
    setNotes(booking?.notes || '');
    setAutoConfirm(true);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // slots for service + date
  useEffect(() => {
    if (!open || !serviceUid || !date) { setSlots([]); setSlotReason(null); setSlotError(null); setNextDates([]); return; }
    let cancelled = false;
    setSlotLoading(true);
    appointmentsService.availableSlots(serviceUid, date)
      .then(async (r) => {
        if (cancelled) return;
        const list = r.slots ?? [];
        setSlotError(null);
        setSlotReason(r.reason ?? null);
        setNextDates([]);
        if (!edit && !list.some((x) => x.available)) {
          try {
            const d = await appointmentsService.availableDates(serviceUid, 3, date);
            if (cancelled) return;
            const days = d.dates ?? [];
            if (!dateChosen.current && days[0] && days[0].date !== date) { setDate(days[0].date); return; }
            setNextDates(days.filter((x) => x.date !== date));
          } catch { /* the reason alone is still useful */ }
        }
        setSlots(list);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setSlots([]); setSlotReason(null); setNextDates([]);
        setSlotError(e?.response?.data?.message ?? "This service's times couldn't be loaded. Try again.");
      })
      .finally(() => { if (!cancelled) setSlotLoading(false); });
    return () => { cancelled = true; };
  }, [open, serviceUid, date, edit]);

  const activeServices = useMemo(
    () => services.filter((s) => s.isActive || s.serviceUid === booking?.serviceUid),
    [services, booking],
  );

  const pickDate = (d: string) => { dateChosen.current = true; setDate(d); setSlot(null); clearErr('date'); };
  const dayChip = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  const nextDays = nextDates.length > 0 && (
    <div className="mt-2.5 flex flex-wrap items-center justify-center gap-1.5">
      <span className="text-[11.5px] text-text-muted">Next days with times:</span>
      {nextDates.map((d) => (
        <button
          key={d.date}
          type="button"
          onClick={() => pickDate(d.date)}
          className="rounded-full border border-border bg-surface px-2.5 py-1 text-[11.5px] font-medium text-text-primary transition-colors hover:border-primary/60 hover:bg-primary-soft/40 cursor-pointer"
        >
          {dayChip(d.date)}
        </button>
      ))}
    </div>
  );

  const dateObj = date ? new Date(`${date}T00:00:00`) : undefined;
  const niceDate = dateObj
    ? dateObj.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    : 'Pick a date';

  const save = async () => {
    // inline validation — highlight fields, message below (slot: next to caption)
    const errs: BkErrors = {};
    if (!serviceUid) errs.service = 'Pick a service.';
    if (!date) errs.date = 'Pick a date.';
    if (!slot) errs.slot = 'Pick a time slot.';
    const phoneDigits = phone.replace(/\D/g, '');
    if (!edit) {
      if (!name.trim()) errs.name = 'Customer name is required.';
      if (!phoneDigits) errs.phone = 'Phone number is required.';
      if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) errs.email = 'Enter a valid email address.';
    }
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) return;

    const startTime = Date.now();
    setSaving(true);
    setOverlayPhase('processing');
    setOverlayVisible(true);

    let ok = true;
    let message = '';
    try {
      if (edit) {
        await appointmentsService.updateBooking(booking!.bookingUid, {
          bookingDate: date, slotStart: slot!.start, slotEnd: slot!.end, notes,
          customerName: name.trim(), customerPhone: phoneDigits, customerEmail: email.trim(),
        });
      } else {
        await appointmentsService.createBooking({
          serviceUid, bookingDate: date, slotStart: slot!.start, slotEnd: slot!.end,
          customerName: name.trim(), customerPhone: phoneDigits, customerEmail: email.trim(),
          notes, autoConfirm, source: 'ADMIN',
        });
      }
    } catch (e: any) {
      ok = false;
      message = e?.response?.data?.message ?? 'Try again.';
    }
    setSaving(false);

    // minimum loader duration — fast saves must not flicker
    const elapsed = Date.now() - startTime;
    if (elapsed < 1000) await new Promise((res) => setTimeout(res, 1000 - elapsed));

    if (!ok) {
      setOverlayVisible(false);
      toast.error(`Couldn't save booking — ${message}`);
      return;
    }
    setOverlayPhase('complete');
    await new Promise((res) => setTimeout(res, 600));
    onClose();
    setOverlayVisible(false);
    toast.success(edit ? 'Booking updated' : 'Booking created');
    onSaved();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={edit ? `Edit booking${booking?.bookingNo ? ` — ${booking.bookingNo}` : ''}` : 'New booking'}
        widthClass="max-w-[880px]"
      >
        <p className="text-[12px] text-text-muted -mt-2 mb-4">
          {edit ? 'Reschedule or update the notes — the customer details stay.' : 'Pick a service, choose a slot, add the customer.'}
        </p>

        <div className="grid gap-6 grid-cols-1 md:grid-cols-[1.05fr_1fr]">
          {/* ── left: what & when ── */}
          <div className="flex flex-col gap-5 rounded-2xl border border-border bg-surface-alt/40 p-4">
            <div>
              <Label step={1}>Service</Label>
              <ServicePicker
                services={activeServices}
                value={serviceUid}
                onChange={(v) => { setServiceUid(v); setSlot(null); clearErr('service'); }}
                disabled={edit}
                error={!!errors.service}
              />
              {errors.service && <div className="text-[11px] text-danger font-medium mt-1">{errors.service}</div>}
            </div>

            <div>
              <Label step={2}>Date</Label>
              <Input
                type="date"
                value={date}
                min={ymd(new Date())}
                invalid={!!errors.date}
                onChange={(e) => pickDate(e.target.value)}
              />
              {errors.date && <div className="text-[11px] text-danger font-medium mt-1">{errors.date}</div>}
            </div>

            <div>
              <Label step={3}>
                Time slot
                {errors.slot && <span className="text-danger normal-case tracking-normal font-medium ml-1">— {errors.slot}</span>}
              </Label>
              {!serviceUid ? (
                <div className="rounded-xl border border-dashed border-border text-[12px] text-text-muted px-3 py-4 text-center">
                  Pick a service first — its available slots appear here.
                </div>
              ) : slotLoading ? (
                <div className="flex items-center gap-2 text-[13px] text-text-muted py-4"><Loader2 size={15} className="animate-spin" /> Loading slots…</div>
              ) : slotError ? (
                <div className="rounded-xl border border-danger/30 bg-danger-soft px-3 py-3 text-[12.5px] text-danger">{slotError}</div>
              ) : slots.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border px-3 py-3 text-center">
                  <p className="text-[12.5px] text-text-primary">{slotReason ?? 'No times on this day.'}</p>
                  {nextDays}
                </div>
              ) : (
                <>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${serviceUid}-${date}`}
                    initial="hidden"
                    animate="show"
                    exit={{ opacity: 0, x: -18, transition: { duration: 0.12 } }}
                    variants={{ hidden: {}, show: { transition: { staggerChildren: 0.014 } } }}
                    className="grid grid-cols-4 gap-1.5"
                  >
                    {slots.map((s) => {
                      const sel = slot?.start === s.start && slot?.end === s.end;
                      return (
                        <motion.button
                          key={s.start}
                          variants={{ hidden: { opacity: 0, x: 22 }, show: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 500, damping: 32 } } }}
                          type="button"
                          disabled={!s.available && !sel}
                          onClick={() => { setSlot(s); clearErr('slot'); }}
                          className={clsx(
                            'border rounded-lg px-1 py-[7px] text-[12px] font-medium transition-colors cursor-pointer',
                            sel ? 'bg-primary text-on-primary border-primary shadow-soft'
                              : s.available ? 'bg-surface border-border text-text-primary hover:border-primary/60 hover:bg-primary-soft/40'
                              : 'bg-surface-alt/60 border-border text-text-muted/50 line-through cursor-not-allowed',
                          )}
                        >
                          {to12h(s.start)}
                        </motion.button>
                      );
                    })}
                  </motion.div>
                </AnimatePresence>
                {!slots.some((s) => s.available) && (
                  <div className="mt-2.5 text-center">
                    <p className="text-[12px] text-text-muted">{slotReason ?? 'No times left on this day.'}</p>
                    {nextDays}
                  </div>
                )}
                </>
              )}
            </div>
          </div>

          {/* ── right: customer details ── */}
          <div className="flex flex-col gap-5 rounded-2xl border border-border bg-surface-alt/40 p-4">
            <div className="flex items-center gap-2 -mb-1">
              <span className="w-7 h-7 rounded-lg bg-primary-soft text-primary flex items-center justify-center"><User size={14} /></span>
              <span className="text-[13px] font-bold text-text-primary">Customer details</span>
            </div>
            <div>
              <Label>Customer name</Label>
              <Input
                value={name}
                invalid={!!errors.name}
                onChange={(e) => { setName(e.target.value); clearErr('name'); }}
                placeholder="e.g. Ahmed Ali"
              />
              {errors.name && <div className="text-[11px] text-danger font-medium mt-1">{errors.name}</div>}
            </div>
            <div>
              <Label>Phone</Label>
              <PhoneField
                value={phone}
                onChange={(v) => { setPhone(v); clearErr('phone'); }}
                invalid={!!errors.phone}
              />
              {errors.phone && <div className="text-[11px] text-danger font-medium mt-1">{errors.phone}</div>}
            </div>
            <div>
              <Label>Email <span className="normal-case font-medium text-text-muted/70">(optional)</span></Label>
              <Input
                type="email"
                value={email}
                invalid={!!errors.email}
                onChange={(e) => { setEmail(e.target.value); clearErr('email'); }}
                placeholder="name@example.com"
              />
              {errors.email && <div className="text-[11px] text-danger font-medium mt-1">{errors.email}</div>}
            </div>
            <div>
              <Label icon={StickyNote}>Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the team should know…" />
            </div>
            {!edit && (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-border px-3.5 py-2.5 mt-auto">
                <span>
                  <span className="block text-[13px] font-semibold text-text-primary">Confirm immediately</span>
                  <span className="block text-[11px] text-text-muted">Off = booking starts as Pending</span>
                </span>
                <Toggle checked={autoConfirm} onChange={setAutoConfirm} />
              </div>
            )}
          </div>
        </div>

        {/* footer */}
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-border mt-5">
          <span className={clsx(
            'inline-flex items-center gap-2 text-[12px] rounded-full px-3.5 py-1.5',
            slot ? 'bg-primary-soft text-text-primary font-medium' : 'bg-surface-alt text-text-muted',
          )}>
            <Clock size={12} className={slot ? 'text-primary' : undefined} />
            {slot ? <>{to12h(slot.start)} – {to12h(slot.end)} · {niceDate}</> : 'No slot selected yet'}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} loading={saving}>
              {edit ? 'Save changes' : 'Create booking'}
            </Button>
          </div>
        </div>
      </Modal>

      <SaveProgressOverlay
        isVisible={overlayVisible}
        phase={overlayPhase}
        title={edit ? 'Saving booking…' : 'Creating booking…'}
        subtitle="Reserving the slot…"
        completeTitle={edit ? 'Booking updated!' : 'Booking created!'}
        completeSubtitle={edit ? 'The changes are saved.' : 'The slot is reserved.'}
      />
    </>
  );
};

export default BookingDialog;
